// Copyright (c) 2020 WiseTime. All rights reserved.

import { concat, debounce, Observable, of, timer } from "rxjs"
import { Action } from "redux"
import { catchError, filter, map, mergeMap, withLatestFrom } from "rxjs/operators"
import { t } from "@lingui/macro"
import { create } from "@bufbuild/protobuf"
import * as Actions from "../actions"
import * as TimelineSlice from "../slices/timeline"
import { showSnackBar } from "../../snackbar/actions"
import { prepareListDaySummaryRequest } from "./daySummaries"
import type { RootState } from "../../../app/store"
import { Code, executorMutation, Init, isConnectError, timelineService } from "../../../app/rpc"
import {
  TimeGroup,
  UpdateTimeGroupDurationRequestSchema,
} from "../../../generated/wisetime/user/timeline/v4/timeline_pb"

type UpdateTimeGroupDurationRpc = (request: Init<typeof UpdateTimeGroupDurationRequestSchema>) => Observable<TimeGroup>

export const updateTimeGroupDuration = (updateTimeGroupDurationRpc: UpdateTimeGroupDurationRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.updateTimeGroupDuration.match),
      withLatestFrom(state$),
      debounce(() => timer(500)),
      mergeMap(([action, state]) => updateTimeGroupDurationRpc(action.payload).pipe(
        map(t => t),
        mergeMap(t => concat(
          of(TimelineSlice.upsertTimeGroup(t)),
          of(Actions.listDaySummaries(prepareListDaySummaryRequest(state.timeline.activeDate.toString())))
        )),
        catchError(e => handleError(e, action.payload.timeGroupId ?? "")),
      )),
    )

const handleError = (e: unknown, timeGroupId: string): Observable<Action> => {
  const actions: Action[] = [
    TimelineSlice.setFailedTimeGroupId(timeGroupId),
  ]
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.NotFound) {
      // refresh the timeline since it's likely that we have a stale cache
      actions.push(
        showSnackBar({
          message: t({
            id: "msg_update_time_group_duration_not_found",
            message: "Failed to update timesheet duration. Could not find the target timesheet.",
          }),
          messageType: "ERROR",
        }),
        Actions.onTimelineOutOfSync(),
      )
      return concat(actions)
    }
    if (code === Code.PermissionDenied) {
      actions.push(
        showSnackBar({
          message: t({
            id: "msg_update_time_group_duration_permission_denied",
            message: "Failed to update timesheet duration. You do not have permission to update the timesheet.",
          }),
          messageType: "ERROR",
        })
      )
      return concat(actions)
    }
    if (code === Code.FailedPrecondition) {
      // refresh the timeline since it's likely that we have a stale cache
      actions.push(
        showSnackBar({
          message: t({
            id: "msg_update_time_group_duration_failed_precondition",
            message: "Failed to update timesheet duration. The timesheet is already posted and can't be changed anymore.",
          }),
          messageType: "ERROR",
        }),
        Actions.onTimelineOutOfSync(),
      )
      return concat(actions)
    }
  }
  actions.push(
    showSnackBar({
      message: t({
        id: "msg_update_time_group_duration_failed",
        message: "Failed to update the timesheet duration. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
      }),
      messageType: "ERROR",
    })
  )
  return concat(actions)
}

const updateTimeGroupDurationRpc = (request: Init<typeof UpdateTimeGroupDurationRequestSchema>): Observable<TimeGroup> =>
  executorMutation(() => timelineService.updateTimeGroupDuration(create(UpdateTimeGroupDurationRequestSchema, request)))

export default updateTimeGroupDuration(updateTimeGroupDurationRpc)
